// src/pages/documents/TandaMasukDetailPage/TandaMasukDetailPage.jsx
import React, { useEffect, useState, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import OrdersAPI from '../../../api/v3/orders-api-v3'
import {
  pdf,
  Document,
  Page,
  Text,
  View,
  StyleSheet
} from '@react-pdf/renderer'

const BASE = '/app/sales/bagspa'

// ---------- PDF styles ----------
const styles = StyleSheet.create({
  page: { padding: 28, fontSize: 11, fontFamily: 'Helvetica' },
  h1: { fontSize: 16, textAlign: 'center', marginBottom: 2 },
  h2: { fontSize: 12, textAlign: 'center', marginBottom: 10 },
  row: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 6 },
  sectionTitle: { marginTop: 8, fontWeight: 700 },
  table: { marginTop: 6, borderTopWidth: 1, borderBottomWidth: 1, borderColor: '#000' },
  tr: { flexDirection: 'row', borderBottomWidth: 1, borderColor: '#999' },
  th: { fontWeight: 700, padding: 5, flex: 1, textAlign: 'center' },
  td: { padding: 5, flex: 1, textAlign: 'center' },
  notesBox: { marginTop: 6, borderWidth: 1, borderColor: '#bbb', padding: 5, minHeight: 32 },
  small: { fontSize: 9, marginTop: 8, lineHeight: 1.3 },
  totalRow: { flexDirection: 'row', borderTopWidth: 1, borderColor: '#000', marginTop: 2 },
  totalText: { padding: 5, fontWeight: 700, flex: 5, textAlign: 'right' },
  totalValue: { padding: 5, fontWeight: 700, flex: 1, textAlign: 'center' }
})

function formatIDR(num) {
  return `Rp ${Number(num || 0).toLocaleString()}`
}

// ---------- PDF generator ----------
function TandaMasukPDF({ order }) {
  const o = order || {}
  const customer = o.customer || {}
  const items = o.order_items || []
  const orderNo = o.order_no || `ORD-${o.id}`
  const createdAt = new Date(o.created_at || Date.now()).toLocaleDateString()

  // Assume each item has base_price/fare (from order_category)
  const totalBase = items.reduce((sum, it) => sum + Number(it.base_price || 0), 0)
  const advance = Number(o.advance_payment_price || totalBase)
  const finalP = Number(o.final_fixed_price || totalBase)
  const remain = finalP - advance

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Text style={styles.h1}>BELLE & KATE – Luxury Bag Spa</Text>
        <Text style={styles.h2}>TANDA TERIMA BARANG</Text>

        <View style={styles.row}>
          <Text>No: {orderNo}</Text>
          <Text>Tanggal: {createdAt}</Text>
        </View>

        <Text style={styles.sectionTitle}>Data Customer</Text>
        <Text>Nama: {customer.name || '-'}</Text>
        <Text>No. HP: {customer.phone || '-'}</Text>
        <Text>Email: {customer.email || '-'}</Text>

        <Text style={styles.sectionTitle}>Detail Barang</Text>
        <View style={styles.table}>
          <View style={styles.tr}>
            <Text style={[styles.th, { flex: 0.4 }]}>No</Text>
            <Text style={styles.th}>Brand</Text>
            <Text style={styles.th}>Kategori</Text>
            <Text style={styles.th}>Warna</Text>
            <Text style={styles.th}>Material</Text>
            <Text style={styles.th}>Keterangan</Text>
            <Text style={styles.th}>Harga (Rp)</Text>
          </View>
          {items.length > 0 ? (
            <>
              {items.map((it, i) => {
                const p = it || {}
                const price = it.base_price || it.order_category_price || 0
                return (
                  <View key={i} style={styles.tr}>
                    <Text style={[styles.td, { flex: 0.4 }]}>{i + 1}</Text>
                    <Text style={styles.td}>{p.brand_name || '-'}</Text>
                    <Text style={styles.td}>{p.category_name || '-'}</Text>
                    <Text style={styles.td}>{p.colour_name || '-'}</Text>
                    <Text style={styles.td}>{p.material_name || '-'}</Text>
                    <Text style={styles.td}>{p.extra_info || '-'}</Text>
                    <Text style={styles.td}>{price ? price.toLocaleString() : '-'}</Text>
                  </View>
                )
              })}
              {/* TOTAL ROW */}
              <View style={styles.totalRow}>
                <Text style={styles.totalText}>Total</Text>
                <Text style={styles.totalValue}>{totalBase.toLocaleString()}</Text>
              </View>
            </>
          ) : (
            <View style={styles.tr}>
              <Text style={[styles.td, { flex: 6 }]}>Tidak ada data barang</Text>
            </View>
          )}
        </View>

        <Text style={styles.sectionTitle}>Kondisi Awal / Notes:</Text>
        <View style={styles.notesBox}><Text>{o.additional_notes || '-'}</Text></View>

        <Text style={styles.sectionTitle}>Layanan yang Dipilih:</Text>
        <View style={styles.notesBox}><Text>{o.order_category_name || o.order_category?.name || '-'}</Text></View>

        <Text style={styles.sectionTitle}>Pembayaran</Text>
        <Text>Subtotal (Total Barang): {formatIDR(totalBase)}</Text>
        <Text>Advance Payment: {formatIDR(advance)}</Text>
        <Text>Final Payment: {formatIDR(finalP)}</Text>
        <Text>Sisa Payment: {formatIDR(remain)}</Text>

        <Text style={styles.sectionTitle}>Catatan & Syarat Ketentuan</Text>
        <Text style={styles.small}>
          1. Customer wajib menyimpan tanda terima ini untuk pengambilan barang.{'\n'}
          2. Barang yang sudah diterima dianggap sesuai dengan kondisi di atas.{'\n'}
          3. Belle & Kate tidak bertanggung jawab atas kerusakan akibat usia/kondisi material yang rapuh.{'\n'}
          4. Ganti rugi tidak melebihi nilai yang tertera di invoice.{'\n'}
          5. Tidak bertanggung jawab atas barang berharga yang tertinggal.{'\n'}
          6. Barang tidak diambil 30 hari setelah pemberitahuan bukan tanggung jawab kami.{'\n'}
          7. Force majeure dikecualikan.{'\n'}
          8. Dengan pembayaran, customer setuju syarat & ketentuan meski tanpa tanda tangan.
        </Text>

        <View style={{ marginTop: 24, flexDirection: 'row', justifyContent: 'space-between' }}>
          <Text>Customer: _____________________</Text>
          <Text>Staff Belle & Kate: _____________________</Text>
        </View>
      </Page>
    </Document>
  )
}

// ---------- MAIN PAGE ----------
export default function TandaMasukDetailPage() {
  const { id } = useParams()
  const [order, setOrder] = useState(null)
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    try {
      setLoading(true)
      const json = await OrdersAPI.show(id)
      setOrder(json?.item || json)
    } catch (e) {
      setErr(e.message)
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => { load() }, [load])

  async function onPrint() {
    try {
      const blob = await pdf(<TandaMasukPDF order={order} />).toBlob()
      const url = URL.createObjectURL(blob)
      window.open(url, '_blank')
    } catch (e) {
      alert(`Gagal membuat PDF: ${e.message}`)
    }
  }

  if (loading) return <div>Loading…</div>
  if (err) return <div className="alert alert-danger">{err}</div>
  if (!order) return <div>Not found.</div>

  const c = order.customer || {}
  const items = order.order_items || []
  const totalBase = items.reduce((sum, it) => sum + Number(it.base_price || 0), 0)
  const adv = Number(order.advance_payment_price || totalBase)
  const fin = Number(order.final_fixed_price || totalBase)
  const remain = fin - adv

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">Tanda Masuk + Invoice — {order.order_no}</h3>
        <div className="btn-group">
          <button className="btn btn-primary" onClick={onPrint}>Generate PDF</button>
          <Link className="btn btn-light" to={`${BASE}/documents/tanda-masuk`}>&larr; Back</Link>
        </div>
      </div>

      {/* Header card */}
      <div className="card mb-3">
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <div><b>Customer:</b> {c.name || '-'} ({c.email || '-'})</div>
              <div><b>Phone:</b> {c.phone || '-'}</div>
              <div><b>Order No:</b> <span className="text-monospace">{order.order_no}</span></div>
              <div><b>Order Status:</b> <span className="text-monospace">{order.status}</span></div>
              <div><b>Tanggal:</b> {(order.created_at || '').slice(0, 10)}</div>
              <div><b>Layanan:</b> {order.order_category_name || '—'}</div>
              <div><b>Estimasi Selesai:</b> {order.estimated_completion_date || '—'}</div>
            </div>
            <div className="col-md-6">
              <div><b>Subtotal:</b> {formatIDR(totalBase)}</div>
              <div><b>Advance Payment:</b> {formatIDR(adv)}</div>
              <div><b>Final Payment:</b> {formatIDR(fin)}</div>
              <div><b>Sisa Payment:</b> {formatIDR(remain)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Items */}
      <div className="card mb-3">
        <div className="card-body">
          <h5 className="mb-3">Detail Barang</h5>
          {items.length ? (
            <table className="table table-sm table-bordered">
              <thead>
                <tr>
                  <th>No</th>
                  <th>Brand</th>
                  <th>Kategori</th>
                  <th>Warna</th>
                  <th>Material</th>
                  <th>Keterangan</th>
                  <th>Harga (Rp)</th>
                  <th>Gambar</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => {
                  const p = it || {}
                  const imgs = (p.product_images || []).slice(0, 4)
                  const price = it.base_price || it.order_category_price || 0
                  return (
                    <tr key={it.id || i}>
                      <td>{i + 1}</td>
                      <td>{p.brand_name || '-'}</td>
                      <td>{p.category_name || '-'}</td>
                      <td>{p.colour_name || '-'}</td>
                      <td>{p.material_name || '-'}</td>
                      <td>{p.extra_info || '-'}</td>
                      <td>{price ? price.toLocaleString() : '-'}</td>
                      <td>
                        <div className="d-flex flex-wrap" style={{ gap: 6 }}>
                          {imgs.length ? imgs.map(img => (
                            <img key={img.id} src={img.url} alt={img.kind} title={img.kind}
                              style={{ width: 54, height: 54, objectFit: 'cover', borderRadius: 6, border: '1px solid #ddd' }} />
                          )) : <span className="text-muted small">No images</span>}
                        </div>
                      </td>
                    </tr>
                  )
                })}
                <tr>
                  <td colSpan={5} className="text-right font-weight-bold">Total</td>
                  <td className="font-weight-bold">{totalBase.toLocaleString()}</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          ) : (
            <div className="text-muted">No items.</div>
          )}
        </div>
      </div>

      {/* Notes */}
      <div className="card mb-3">
        <div className="card-body">
          <h6 className="mb-2">Kondisi Awal / Notes</h6>
          <div className="border rounded p-2 mb-3">{order.additional_notes || <span className="text-muted">—</span>}</div>
          <h6 className="mb-2">Keterangan Tambahan</h6>
          <div className="border rounded p-2">{order.extra_info || <span className="text-muted">—</span>}</div>
        </div>
      </div>
    </div>
  )
}
